import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Download, FileText, QrCode, Users } from 'lucide-react';
import { cn } from '@easymoving/ui';
import { daysUntil } from '../../lib/query/readiness';

/** The hero's main actions — the document-producing tools of the move. */
const ACTIONS = [
  { to: '/app/report', icon: FileText, key: 'move.actionReport' },
  { to: '/app/boxes/labels', icon: QrCode, key: 'move.actionLabels' },
  { to: '/app/export', icon: Download, key: 'move.actionExport' },
  { to: '/app/share', icon: Users, key: 'move.actionShare' },
] as const;

function Ring({ score }: { score: number }) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="relative size-24 shrink-0">
      <svg viewBox="0 0 80 80" className="size-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" className="stroke-white/20" strokeWidth="7" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          className="stroke-white"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-2xl tnum text-white">
        {score}%
      </span>
    </div>
  );
}

export function ReadinessCard({
  score,
  moveDate,
  hasProfile,
  className,
}: {
  score: number;
  moveDate?: number;
  hasProfile: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const days = moveDate != null ? daysUntil(moveDate, Date.now()) : null;
  const dateLine =
    days == null
      ? null
      : days > 0
        ? t('move.daysLeft', { count: days })
        : days === 0
          ? t('move.today')
          : t('move.past');

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl p-6 sm:p-7 text-white shadow-pop',
        'bg-gradient-to-br from-petrol-deep to-petrol',
        className,
      )}
    >
      {/* soft decorative glow */}
      <div className="pointer-events-none absolute -top-16 -end-12 size-48 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex h-full flex-col">
        <div className="flex flex-1 items-center gap-5 sm:gap-6">
          <Ring score={score} />
          <div className="flex-1 min-w-0">
            <div className="smallcaps text-white/70">{t('move.readiness')}</div>
            {dateLine ? (
              <div className="font-display text-xl sm:text-2xl tracking-snug mt-1">{dateLine}</div>
            ) : (
              <div className="text-white/85 text-[15px] leading-relaxed mt-1">{t('move.intro')}</div>
            )}
            <Link
              to="/app/move"
              className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors px-3.5 py-1.5 text-[13px] font-medium"
            >
              {hasProfile ? t('move.editCta') : t('move.setupCta')}
              <ChevronLeft className="size-4 ltr:rotate-180" />
            </Link>
          </div>
        </div>

        {/* main actions — the move's documents, one tap from the hero */}
        <div
          className="mt-5 grid grid-cols-4 gap-2 border-t border-white/15 pt-4"
          data-testid="readiness-actions"
        >
          {ACTIONS.map(({ to, icon: Icon, key }) => (
            <Link
              key={to}
              to={to}
              className="flex flex-col items-center gap-1.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors px-1 py-2.5 text-center"
            >
              <Icon className="size-5" />
              <span className="text-[11.5px] font-medium leading-tight">{t(key)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
